import { Link } from 'react-router-dom'
import { Info, ChevronLeft, ChevronRight } from 'lucide-react'
import type { DocsArticle } from '@/routes/documentation/_data'

interface DocsContentProps {
  article: DocsArticle
  allArticles: DocsArticle[]
}

export function DocsContent({ article, allArticles }: DocsContentProps) {
  const currentIndex = allArticles.findIndex((a) => a.slug === article.slug)
  const prev = currentIndex > 0 ? allArticles[currentIndex - 1] : null
  const next = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null

  return (
    <div className="px-6 md:px-12 lg:px-16 py-8 md:py-12 max-w-3xl">
      <h1 className="text-3xl font-medium tracking-tighter">{article.title}</h1>
      <p className="text-lg text-muted-foreground mt-2 mb-8">{article.description}</p>

      {article.sections.map((section) => (
        <div key={section.id}>
          <h2
            id={section.id}
            className="text-xl font-semibold mt-10 mb-4 scroll-mt-24"
          >
            {section.heading}
          </h2>
          <div className="text-muted-foreground leading-relaxed">
            {section.content}
          </div>

          {section.bullets && (
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-3">
              {section.bullets.map((bullet, i) => (
                <li key={i}>{bullet}</li>
              ))}
            </ul>
          )}

          {section.tip && (
            <div className="bg-accent border border-border rounded-lg p-4 mt-4 flex gap-3">
              <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">{section.tip}</p>
            </div>
          )}

          {section.image && (
            <figure className="mt-4">
              <img
                src={section.image.src}
                alt={section.image.alt}
                className="rounded-lg border border-border shadow-sm w-full"
              />
              {section.image.caption && (
                <figcaption className="text-sm text-muted-foreground text-center mt-2">
                  {section.image.caption}
                </figcaption>
              )}
            </figure>
          )}
        </div>
      ))}

      {/* Prev / Next navigation */}
      <div className="flex justify-between border-t border-border pt-6 mt-12">
        {prev ? (
          <Link
            to={`/documentation/${prev.slug}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            {prev.title}
          </Link>
        ) : (
          <div />
        )}
        {next ? (
          <Link
            to={`/documentation/${next.slug}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {next.title}
            <ChevronRight className="h-4 w-4" />
          </Link>
        ) : (
          <div />
        )}
      </div>
    </div>
  )
}
